import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function StoryHero() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("story");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="story-hero"
      ref={sectionRef}
      className="relative pt-24 md:pt-32 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20conceptual%20illustration%20of%20a%20modern%20metropolitan%20hub%20bridging%20east%20and%20west%2C%20sleek%20silver%20and%20warm%20golden%20digital%20data%20streams%20flowing%20through%20elegant%20skyscrapers%20toward%20a%20glowing%20horizon%2C%20abstract%20representation%20of%20global%20connectivity%20with%20warm%20red%20and%20silver%20light%20particles%2C%20cinematic%20atmosphere%20with%20warm%20tones%2C%20no%20blue%20or%20purple%20colors%2C%20minimalist%20editorial%20illustration%20style%2C%20sense%20of%20forward%20momentum%20and%20technological%20ambition%2C%20clean%20composition&width=1600&height=900&seq=ax-story-bg-3&orientation=landscape"
          alt="AX Axis Global Vision"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white/85" />
      </div>

      <div className="relative px-6 md:px-12 lg:px-20 max-w-5xl mx-auto">
        <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
          <span className="inline-block border border-ax-dark/20 rounded-full px-4 py-1 text-xs font-medium text-ax-dark/60 mb-8 tracking-wide">
            {t("storyHero.label")}
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ax-dark font-bold leading-tight mb-8">
            {t("storyHero.title")}
            <span className="text-ax-red">{t("storyHero.titleAccent")}</span>
          </h1>
        </div>

        <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-200">
          <p className="text-ax-dark/70 text-lg md:text-xl leading-relaxed max-w-3xl">
            {t("storyHero.description")}
          </p>
        </div>

        <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-400 mt-12 flex items-center gap-8">
          <div className="h-px flex-1 bg-gradient-to-r from-ax-red/40 to-transparent" />
          <span className="text-ax-dark/40 text-sm tracking-widest">{t("storyHero.bottomLine")}</span>
          <div className="h-px flex-1 bg-gradient-to-l from-ax-red/40 to-transparent" />
        </div>
      </div>
    </section>
  );
}